import { Navigate, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import KeepLink from '../components/KeepLink';
import { useGraphIndex } from '../hooks/useGraphIndex';
import { useLang } from '../hooks/useLang';
import type { PaalSlug } from '../data/types';
import './Theme.css';

const PAAL_ORDER: PaalSlug[] = ['aram', 'porul', 'inbam'];

export default function Theme() {
    const { slug } = useParams<{ slug: string }>();
    const graph = useGraphIndex();
    const [lang] = useLang();

    const view = useMemo(() => {
        if (!graph.data || !slug) return null;
        const theme = graph.data.themes.find((t) => t.slug === slug);
        if (!theme) return { theme: null };
        const kuralByN = new Map(graph.data.kurals.map((k) => [k.n, k]));
        const members = theme.member_kural_ns
            .map((n) => kuralByN.get(n))
            .filter((k): k is NonNullable<typeof k> => k !== undefined);

        // Group by adhigaram (traversal order = ascending adhigaram_n).
        const byAdh = new Map<
            number,
            { titleEn: string; titleTa: string; kurals: typeof members }
        >();
        for (const k of members) {
            const list = byAdh.get(k.adhigaram_n);
            if (list) {
                list.kurals.push(k);
            } else {
                byAdh.set(k.adhigaram_n, {
                    titleEn: k.adhigaram_title_en,
                    titleTa: k.adhigaram_title_ta,
                    kurals: [k],
                });
            }
        }
        const groups = Array.from(byAdh.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([n, v]) => ({ adhigaram_n: n, ...v }));

        // Paal distribution: raw counts + normalisation for the CSS histogram.
        const paalCounts: Record<PaalSlug, number> = {
            aram: 0,
            porul: 0,
            inbam: 0,
        };
        for (const k of members) paalCounts[k.paal]++;
        const maxCount = Math.max(1, ...Object.values(paalCounts));
        const paalTitles = new Map(
            graph.data.paals.map((p) => [p.slug, { en: p.title_en, ta: p.title_ta }])
        );

        return { theme, members, groups, paalCounts, maxCount, paalTitles };
    }, [graph.data, slug]);

    if (graph.error) {
        return (
            <div className="focus-error" role="alert">
                <p>Sorry — we couldn't load the corpus index. Refresh to try again.</p>
            </div>
        );
    }
    if (graph.loading || !view) {
        return (
            <div className="focus-loading" aria-live="polite">
                <span className="focus-loading__dot" aria-hidden="true" />
                <span>Loading theme…</span>
            </div>
        );
    }
    if (view.theme === null) {
        return <Navigate to="/" replace />;
    }
    const { theme, groups, paalCounts, maxCount, paalTitles, members } = view;

    return (
        <div className="theme-view">
            <header className="theme-view__head">
                <p className="theme-view__eyebrow">Theme</p>
                <h1 className="theme-view__title">
                    {lang === 'ta' ? (
                        <>
                            <span lang="ta" className="theme-view__title-lead">
                                {theme.title_ta}
                            </span>
                            <span className="theme-view__title-sub">{theme.title_en}</span>
                        </>
                    ) : (
                        <>
                            <span className="theme-view__title-lead">{theme.title_en}</span>
                            <span lang="ta" className="theme-view__title-sub">
                                {theme.title_ta}
                            </span>
                        </>
                    )}
                </h1>
                <p className="theme-view__desc">{theme.description}</p>
                <p className="theme-view__count">
                    {members!.length} {members!.length === 1 ? 'kural' : 'kurals'} across{' '}
                    {groups!.length} {groups!.length === 1 ? 'adhigaram' : 'adhigarams'}.
                </p>
            </header>

            <section
                className="theme-view__hist"
                aria-label="Distribution across the three paals"
            >
                <h2 className="theme-view__section-title">By paal</h2>
                <ul className="hist">
                    {PAAL_ORDER.map((paalSlug) => {
                        const count = paalCounts![paalSlug];
                        const heightPct = (count / maxCount!) * 100;
                        const p = paalTitles!.get(paalSlug);
                        return (
                            <li key={paalSlug} className="hist__item">
                                <div className="hist__bar-wrap">
                                    <div
                                        className="hist__bar"
                                        style={{ height: `${heightPct}%` }}
                                        aria-hidden="true"
                                    />
                                </div>
                                <div className="hist__labels">
                                    <div className="hist__count">{count}</div>
                                    <div className="hist__name">
                                        {lang === 'ta' ? (
                                            <span lang="ta">{p?.ta}</span>
                                        ) : (
                                            p?.en ?? paalSlug
                                        )}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </section>

            <section className="theme-view__list" aria-label="Members">
                <h2 className="theme-view__section-title">Kurals</h2>
                {groups!.map((g) => (
                    <div key={g.adhigaram_n} className="theme-view__group">
                        <h3 className="theme-view__group-title">
                            <span className="theme-view__adh-n">
                                Adhigaram {g.adhigaram_n}
                            </span>
                            {lang === 'ta' ? (
                                <>
                                    <span lang="ta" className="theme-view__adh-ta">
                                        {g.titleTa}
                                    </span>
                                    <span className="theme-view__adh-en">{g.titleEn}</span>
                                </>
                            ) : (
                                <>
                                    <span className="theme-view__adh-en">{g.titleEn}</span>
                                    <span lang="ta" className="theme-view__adh-ta">
                                        {g.titleTa}
                                    </span>
                                </>
                            )}
                        </h3>
                        <ul className="theme-view__kurals">
                            {g.kurals.map((k) => (
                                <li key={k.n}>
                                    <KeepLink
                                        to={`/kural/${k.n}`}
                                        className="theme-view__kural"
                                        aria-label={`Open kural ${k.n}: ${k.first_line_ta}. ${k.gist_en}`}
                                    >
                                        <span className="theme-view__kural-n">Kural {k.n}</span>
                                        <span lang="ta" className="theme-view__kural-ta">
                                            {(k.verse_ta && k.verse_ta.length > 0
                                                ? k.verse_ta
                                                : [k.first_line_ta]
                                            ).map((line, i) => (
                                                <span key={i} className="theme-view__kural-ta-line">
                                                    {line}
                                                </span>
                                            ))}
                                        </span>
                                        <span className="theme-view__kural-gist">{k.gist_en}</span>
                                    </KeepLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </section>
        </div>
    );
}
